/**
 * Utility functions for color coding overtime data based on hours worked
 * Uses strict brackets with density-based shading and theme-aware colors
 */

export interface OvertimeColorScheme {
  backgroundColor: string;
  textColor: string;
  borderColor: string;
  badgeVariant: 'default' | 'secondary' | 'destructive' | 'outline';
}

export interface OvertimeRange {
  label: string;
  minHours: number;
  maxHours: number;
  description: string;
  baseColor: string;
  colorScheme: OvertimeColorScheme;
}

/**
 * Centralized overtime ranges configuration
 * Update these values to change ranges across the entire application
 */
export const OVERTIME_RANGES: OvertimeRange[] = [
  {
    label: '≤40 hours',
    minHours: 0,
    maxHours: 40,
    description: 'Normal (no highlight)',
    baseColor: '0, 0%, ', // gray - no highlight
    colorScheme: {
      backgroundColor: 'bg-gray-50 dark:bg-gray-950/30',
      textColor: 'text-gray-800 dark:text-gray-200',
      borderColor: 'border-gray-200 dark:border-gray-800',
      badgeVariant: 'outline'
    }
  },
  {
    label: '41-55 hours',
    minHours: 41,
    maxHours: 55,
    description: 'Green bracket',
    baseColor: '120, 60%, ', // green
    colorScheme: {
      backgroundColor: 'bg-green-50 dark:bg-green-950/30',
      textColor: 'text-green-800 dark:text-green-200',
      borderColor: 'border-green-200 dark:border-green-800',
      badgeVariant: 'default'
    }
  },
  {
    label: '56-70 hours',
    minHours: 56,
    maxHours: 70,
    description: 'Yellow bracket',
    baseColor: '50, 70%, ', // yellow
    colorScheme: {
      backgroundColor: 'bg-yellow-50 dark:bg-yellow-950/30',
      textColor: 'text-yellow-800 dark:text-yellow-200',
      borderColor: 'border-yellow-200 dark:border-yellow-800',
      badgeVariant: 'outline'
    }
  },
  {
    label: '71-85 hours',
    minHours: 71,
    maxHours: 85,
    description: 'Orange bracket',
    baseColor: '30, 70%, ', // orange
    colorScheme: {
      backgroundColor: 'bg-orange-50 dark:bg-orange-950/30',
      textColor: 'text-orange-800 dark:text-orange-200',
      borderColor: 'border-orange-200 dark:border-orange-800',
      badgeVariant: 'outline'
    }
  },
  {
    label: '>85 hours',
    minHours: 86,
    maxHours: Infinity,
    description: 'Red bracket',
    baseColor: '0, 70%, ', // red
    colorScheme: {
      backgroundColor: 'bg-red-50 dark:bg-red-950/30',
      textColor: 'text-red-800 dark:text-red-200',
      borderColor: 'border-red-200 dark:border-red-800',
      badgeVariant: 'destructive'
    }
  }
];

/**
 * Get row background color based on overtime hours with density-based shading
 * @param hours - Number of overtime hours worked
 * @param isDarkMode - Whether dark mode is active
 * @returns HSL color string or empty string for no highlight
 */
export function getRowColor(hours: number, isDarkMode: boolean): string {
  // Find the appropriate range for the given hours
  const range = OVERTIME_RANGES.find(r => hours >= r.minHours && hours <= r.maxHours);
  
  if (!range || range.maxHours === 40) {
    return ""; // no highlight for normal hours
  }

  const { baseColor, minHours, maxHours } = range;
  const maxRange = maxHours === Infinity ? 100 : maxHours; // Cap at 100 for intensity calculation

  // Normalize intensity between 0 → 1
  const intensity = Math.min(1, Math.max(0, (hours - minHours) / (maxRange - minHours)));

  // Adjust opacity: subtle in light mode, slightly stronger in dark mode
  const alpha = isDarkMode ? 0.15 + intensity * 0.35 : 0.1 + intensity * 0.25;

  return `hsla(${baseColor}${isDarkMode ? "40%" : "60%"}, ${alpha})`;
}

/**
 * Get color scheme for overtime hours based on strict thresholds
 * @param hours - Number of overtime hours worked
 * @returns Color scheme object with background, text, and border colors
 */
export function getOvertimeColorScheme(hours: number): OvertimeColorScheme {
  // Find the appropriate range for the given hours
  const range = OVERTIME_RANGES.find(r => hours >= r.minHours && hours <= r.maxHours);
  
  // Return the color scheme from the range, or default to normal if not found
  return range?.colorScheme || OVERTIME_RANGES[0].colorScheme;
}

/**
 * Get badge styling based on overtime hours
 * @param hours - Number of overtime hours worked
 * @returns CSS classes string for badge
 */
export function getBadgeClasses(hours: number): string {
  const colorScheme = getOvertimeColorScheme(hours);
  return `px-3 py-1.5 ${colorScheme.backgroundColor} ${colorScheme.textColor} ${colorScheme.borderColor} border font-semibold`;
}
